import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = '/api/demo/admin';

function authHeader() {
  const t = localStorage.getItem('token');
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
}

type DemoSession = {
  id: string; lead_name: string; lead_email: string; lead_company?: string;
  lead_phone?: string; use_case?: string; bot_name?: string; voice_name?: string;
  status: string; msg_count: number; call_count: number; score: number;
  score_label: string; expires_at: string; created_at: string; completed_at?: string;
};

type DemoNumber = {
  id: number; number: string; country_code: string; label?: string;
  is_available: number; session_id?: string; lead_name?: string; lead_email?: string;
  created_at: string;
};

type Stats = {
  total: number; active: number; completed: number;
  hot_leads: number; warm_leads: number; total_messages: number;
  avg_messages: number; today: number;
};

const S = {
  card: { background: '#1e293b', borderRadius: 14, padding: 24, border: '1px solid rgba(148,163,184,0.1)', marginBottom: 20 },
  btn: (c = '#2563eb', sm = false) => ({
    background: c, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
    padding: sm ? '6px 14px' : '10px 20px', fontSize: sm ? 12 : 14, fontWeight: 600,
  } as React.CSSProperties),
  input: { background: '#0f172a', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8, padding: '10px 14px', color: '#f1f5f9', fontSize: 14, width: '100%', boxSizing: 'border-box' as const } as React.CSSProperties,
  badge: (c: string) => ({ background: `${c}22`, color: c, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700 }),
};

const STATUS_COLORS: Record<string, string> = { lead: '#94a3b8', knowledge_ready: '#3b82f6', active: '#10b981', completed: '#6366f1', expired: '#ef4444' };
const SCORE_COLORS: Record<string, string> = { Hot: '#ef4444', Warm: '#f59e0b', Cold: '#94a3b8' };

export default function DemoManagement() {
  const [tab, setTab] = useState<'sessions' | 'numbers' | 'stats'>('sessions');
  const [sessions, setSessions] = useState<DemoSession[]>([]);
  const [numbers, setNumbers] = useState<DemoNumber[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [detail, setDetail] = useState<any>(null);
  const [newNumber, setNewNumber] = useState({ number: '', country_code: 'UK', label: '' });
  const [addingNumber, setAddingNumber] = useState(false);
  const [error, setError] = useState('');

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50', offset: '0' });
      if (filterStatus) params.set('status', filterStatus);
      const r = await axios.get(`${API}/sessions?${params}`, authHeader());
      setSessions(r.data.sessions || []);
      setTotal(r.data.total || 0);
    } catch { setError('Failed to load sessions'); }
    finally { setLoading(false); }
  }, [filterStatus]);

  const loadNumbers = useCallback(async () => {
    try { const r = await axios.get(`${API}/numbers`, authHeader()); setNumbers(r.data || []); }
    catch { setError('Failed to load numbers'); }
  }, []);

  const loadStats = useCallback(async () => {
    try { const r = await axios.get(`${API}/stats`, authHeader()); setStats(r.data); }
    catch { setError('Failed to load stats'); }
  }, []);

  useEffect(() => {
    if (tab === 'sessions') loadSessions();
    else if (tab === 'numbers') loadNumbers();
    else if (tab === 'stats') loadStats();
  }, [tab, loadSessions, loadNumbers, loadStats]);

  const loadDetail = async (id: string) => {
    try {
      const r = await axios.get(`${API}/sessions/${id}`, authHeader());
      setDetail(r.data);
    } catch { setError('Failed to load session detail'); }
  };

  const deleteSession = async (id: string) => {
    if (!window.confirm('Delete this demo session? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/sessions/${id}`, authHeader());
      setSessions(s => s.filter(x => x.id !== id));
      if (detail?.id === id) setDetail(null);
    } catch { setError('Delete failed'); }
  };

  const addNumber = async () => {
    if (!newNumber.number.trim()) return;
    setAddingNumber(true); setError('');
    try {
      const r = await axios.post(`${API}/numbers`, newNumber, authHeader());
      setNumbers(n => [r.data, ...n]);
      setNewNumber({ number: '', country_code: 'UK', label: '' });
    } catch (e: any) { setError(e.response?.data?.error || e.message || 'Failed to add number'); }
    finally { setAddingNumber(false); }
  };

  const deleteNumber = async (id: number) => {
    try {
      await axios.delete(`${API}/numbers/${id}`, authHeader());
      setNumbers(n => n.filter(x => x.id !== id));
    } catch { setError('Delete failed'); }
  };

  return (
    <div style={{ color: '#f1f5f9', maxWidth: 1200 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>🤖 Demo Platform</h1>
        <p style={{ color: '#94a3b8', fontSize: 14 }}>
          Manage self-service AI Voice Bot demo sessions and phone number pool.{' '}
          <a href="/demo" target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>Open Demo Page ↗</a>
        </p>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '10px 16px', marginBottom: 16, color: '#fca5a5', fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
          {error} <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {(['sessions', 'numbers', 'stats'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '10px 22px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            background: tab === t ? '#2563eb' : 'rgba(148,163,184,0.1)',
            color: tab === t ? '#fff' : '#94a3b8',
          }}>
            {t === 'sessions' ? `📋 Sessions (${total})` : t === 'numbers' ? `📞 Numbers (${numbers.length})` : '📊 Analytics'}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {tab === 'sessions' && (
          <a
            href={`/api/demo/admin/sessions/export?token=${localStorage.getItem('token') || ''}`}
            download="demo-leads.csv"
            style={{ ...S.btn('#059669', true), textDecoration: 'none', display: 'inline-block', padding: '10px 18px' }}
            onClick={e => {
              // Use fetch with auth header instead of direct href
              e.preventDefault();
              const t = localStorage.getItem('token') || '';
              fetch('/api/demo/admin/sessions/export', { headers: { Authorization: `Bearer ${t}` } })
                .then(r => r.blob()).then(blob => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `demo-leads-${Date.now()}.csv`; a.click();
                  URL.revokeObjectURL(url);
                });
            }}
          >
            ⬇ Export CSV
          </a>
        )}
        <button onClick={() => { if (tab === 'sessions') loadSessions(); else if (tab === 'numbers') loadNumbers(); else loadStats(); }} style={S.btn('#374151', true)}>↻ Refresh</button>
      </div>

      {/* ── SESSIONS TAB ────────────────────────────────────────────────────── */}
      {tab === 'sessions' && (
        <div style={{ display: 'flex', gap: 20 }}>
          <div style={{ flex: detail ? '0 0 55%' : 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {['', 'lead', 'knowledge_ready', 'active', 'completed', 'expired'].map(s => (
                <button key={s} onClick={() => setFilterStatus(s)} style={{
                  padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12,
                  background: filterStatus === s ? '#2563eb' : 'rgba(148,163,184,0.1)',
                  color: filterStatus === s ? '#fff' : '#94a3b8',
                }}>
                  {s || 'All'}
                </button>
              ))}
            </div>

            {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading...</div> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'rgba(15,23,42,0.5)' }}>
                      {['Name / Company', 'Use Case', 'Bot', 'Status', 'Score', 'Msgs', 'Date', ''].map((h, i) => (
                        <th key={i} style={{ padding: '10px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map(s => (
                      <tr key={s.id} style={{ borderBottom: '1px solid rgba(148,163,184,0.05)', cursor: 'pointer' }}
                        onClick={() => loadDetail(s.id)}>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ fontWeight: 600 }}>{s.lead_name}</div>
                          <div style={{ color: '#64748b', fontSize: 11 }}>{s.lead_email}</div>
                          {s.lead_company && <div style={{ color: '#64748b', fontSize: 11 }}>{s.lead_company}</div>}
                        </td>
                        <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{s.use_case || '—'}</td>
                        <td style={{ padding: '10px 12px' }}>{s.bot_name || '—'}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={S.badge(STATUS_COLORS[s.status] || '#94a3b8')}>{s.status}</span>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={S.badge(SCORE_COLORS[s.score_label] || '#94a3b8')}>{s.score_label}</span>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>{s.msg_count}</td>
                        <td style={{ padding: '10px 12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                          {new Date(s.created_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '10px 12px' }} onClick={e => e.stopPropagation()}>
                          <button onClick={() => deleteSession(s.id)} style={S.btn('#ef4444', true)}>Del</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {sessions.length === 0 && !loading && (
                  <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                    No demo sessions yet. Share the demo page link to start collecting leads.
                  </div>
                )}
              </div>
            )}
          </div>

          {detail && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>{detail.lead_name}</div>
                    <div style={{ color: '#64748b', fontSize: 13 }}>{detail.lead_email}</div>
                    {detail.lead_company && <div style={{ color: '#64748b', fontSize: 13 }}>{detail.lead_company}</div>}
                    {detail.lead_phone && <div style={{ color: '#64748b', fontSize: 13 }}>{detail.lead_phone}</div>}
                  </div>
                  <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 18, cursor: 'pointer' }}>✕</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16, fontSize: 13 }}>
                  {([
                    ['Status', <span style={S.badge(STATUS_COLORS[detail.status] || '#94a3b8')}>{detail.status}</span>],
                    ['Score', <span style={S.badge(SCORE_COLORS[detail.score_label] || '#94a3b8')}>{detail.score}/100 — {detail.score_label}</span>],
                    ['Bot Name', detail.bot_name || '—'],
                    ['Voice', detail.voice_name || '—'],
                    ['Messages', detail.msg_count],
                    ['Calls', detail.call_count || 0],
                    ['Use Case', detail.use_case || '—'],
                    ['Industry', detail.lead_industry || '—'],
                    ['Country', detail.lead_country || '—'],
                    ['Call Volume', detail.monthly_call_volume || '—'],
                    ['Current System', detail.current_phone_system || '—'],
                    ['Website', detail.lead_website ? <a href={detail.lead_website} target="_blank" rel="noreferrer" style={{ color: '#60a5fa', fontSize: 11 }}>{detail.lead_website}</a> : '—'],
                  ] as [string, React.ReactNode][]).map(([k, v], i) => (
                    <div key={i} style={{ background: 'rgba(15,23,42,0.5)', borderRadius: 8, padding: '8px 12px' }}>
                      <div style={{ color: '#64748b', fontSize: 11, marginBottom: 2 }}>{k}</div>
                      <div style={{ fontWeight: 600 }}>{v}</div>
                    </div>
                  ))}
                </div>

                {detail.knowledge_files?.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>KNOWLEDGE FILES</div>
                    {detail.knowledge_files.map((f: any, i: number) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'rgba(15,23,42,0.4)', borderRadius: 6, marginBottom: 4, fontSize: 12 }}>
                        <span>{f.filename}</span>
                        <span style={{ color: f.status === 'ready' ? '#10b981' : f.status === 'error' ? '#ef4444' : '#f59e0b' }}>
                          {f.status === 'ready' ? `✓ ${f.chunk_count} chunks` : f.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {detail.messages?.length > 0 && (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>TRANSCRIPT ({detail.messages.length} messages)</div>
                    <div style={{ maxHeight: 300, overflowY: 'auto', background: 'rgba(15,23,42,0.4)', borderRadius: 10, padding: '12px' }}>
                      {detail.messages.map((m: any, i: number) => (
                        <div key={i} style={{ marginBottom: 10, fontSize: 12 }}>
                          <div style={{ fontWeight: 600, color: m.role === 'user' ? '#60a5fa' : '#a78bfa', marginBottom: 2 }}>
                            {m.role === 'user' ? '👤 Visitor' : `🤖 ${detail.bot_name || 'Bot'}`}
                            <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 8 }}>
                              {new Date(m.created_at).toLocaleTimeString()}
                            </span>
                          </div>
                          <div style={{ color: '#cbd5e1' }}>{m.content}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── NUMBERS TAB ──────────────────────────────────────────────────────── */}
      {tab === 'numbers' && (
        <div>
          <div style={S.card}>
            <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: 15 }}>Add Demo Phone Number</h3>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>PHONE NUMBER</label>
                <input style={S.input} placeholder="+44 20 0000 0000" value={newNumber.number}
                  onChange={e => setNewNumber(n => ({ ...n, number: e.target.value }))} />
              </div>
              <div style={{ flex: '0 0 120px' }}>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>COUNTRY</label>
                <select style={S.input} value={newNumber.country_code}
                  onChange={e => setNewNumber(n => ({ ...n, country_code: e.target.value }))}>
                  <option value="UK">UK</option><option value="UAE">UAE</option>
                  <option value="US">US</option><option value="AU">AU</option>
                </select>
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>LABEL (optional)</label>
                <input style={S.input} placeholder="e.g. UK Demo Line" value={newNumber.label}
                  onChange={e => setNewNumber(n => ({ ...n, label: e.target.value }))} />
              </div>
              <div style={{ alignSelf: 'flex-end' }}>
                <button style={S.btn()} onClick={addNumber} disabled={addingNumber || !newNumber.number.trim()}>
                  {addingNumber ? 'Adding...' : '+ Add Number'}
                </button>
              </div>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'rgba(15,23,42,0.5)' }}>
                  {['Number', 'Country', 'Label', 'Status', 'Assigned To', 'Added', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {numbers.map(n => (
                  <tr key={n.id} style={{ borderBottom: '1px solid rgba(148,163,184,0.05)' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700 }}>{n.number}</td>
                    <td style={{ padding: '10px 12px' }}>{n.country_code}</td>
                    <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{n.label || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={S.badge(n.is_available ? '#10b981' : '#f59e0b')}>
                        {n.is_available ? 'Available' : 'In Use'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#64748b', fontSize: 12 }}>
                      {n.lead_name ? `${n.lead_name} (${n.lead_email})` : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#64748b' }}>{new Date(n.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {n.is_available ? (
                        <button onClick={() => deleteNumber(n.id)} style={S.btn('#ef4444', true)}>Remove</button>
                      ) : (
                        <span style={{ color: '#64748b', fontSize: 12 }}>In use</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {numbers.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                No demo numbers yet. Add phone numbers above to display in the demo experience.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── STATS TAB ────────────────────────────────────────────────────────── */}
      {tab === 'stats' && stats && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
            {[
              { label: 'Total Sessions', value: stats.total, color: '#2563eb' },
              { label: 'Active Now', value: stats.active, color: '#10b981' },
              { label: '🔥 Hot Leads', value: stats.hot_leads, color: '#ef4444' },
              { label: 'Today', value: stats.today, color: '#7c3aed' },
            ].map((s, i) => (
              <div key={i} style={S.card}>
                <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 6 }}>{s.label}</div>
                <div style={{ fontSize: 36, fontWeight: 800, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            <div style={S.card}>
              <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: 15 }}>Lead Quality</h3>
              {[
                { label: 'Hot Leads', value: stats.hot_leads, color: '#ef4444' },
                { label: 'Warm Leads', value: stats.warm_leads, color: '#f59e0b' },
                { label: 'Cold Leads', value: Math.max(0, (stats.total || 0) - stats.hot_leads - stats.warm_leads), color: '#94a3b8' },
              ].map((r, i) => (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 13 }}>{r.label}</span>
                    <span style={{ fontWeight: 700, color: r.color }}>{r.value}</span>
                  </div>
                  <div style={{ height: 6, background: 'rgba(148,163,184,0.1)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: r.color, width: `${stats.total ? (r.value / stats.total) * 100 : 0}%`, borderRadius: 3 }} />
                  </div>
                </div>
              ))}
            </div>

            <div style={S.card}>
              <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: 15 }}>Engagement</h3>
              {([
                ['Total Messages', stats.total_messages || 0],
                ['Avg Messages / Session', stats.avg_messages || 0],
                ['Completed Demos', stats.completed],
                ['Conversion Rate', `${stats.total ? Math.round((stats.completed / stats.total) * 100) : 0}%`],
              ] as [string, string | number][]).map(([label, value], i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(148,163,184,0.05)', fontSize: 13 }}>
                  <span style={{ color: '#94a3b8' }}>{label}</span>
                  <span style={{ fontWeight: 700 }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'stats' && !stats && (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading analytics...</div>
      )}
    </div>
  );
}
